/**
 * Fields that are invariant across an asset subtree: `customer` can never be
 * patched onto an asset, and `site` is cascaded to every descendant whenever an
 * asset is reparented. A filter on these fields can never match a child whose
 * parent does not also match, so a parent-scoped tree stays complete for them.
 */
const ASSET_SUBTREE_INVARIANT_FIELDS = ['customer', 'site'];

/** Structural shape so both EntityItemsRequest (api) and the web request can be passed without import cycles. */
export interface AssetTreeFlattenRequest {
    variant?: string;
    filters?: { _id?: string; fieldId?: string; value?: any }[];
}

/**
 * Single source of truth — used by both the API query builder and the web tree
 * grid — for whether an asset tree request must return/render matching assets
 * as a flat list instead of a parent-scoped tree.
 *
 * Filters on fields that can vary inside a subtree (status, asset type, tags…)
 * can match a child whose ancestors do not match; a parent-scoped tree would
 * silently hide those children. Flat results mirror the existing search
 * behaviour. Must be evaluated on the raw client request: the view merge
 * injects baseline filters (e.g. customer.type) into every request.
 *
 * Applies to the children-of-asset embedded view too: it still flattens on a
 * child-level filter, but the API scopes the flat result to the open asset's
 * subtree (TenantAssetEntityDataStrategy) rather than the whole tenant.
 */
export function shouldFlattenAssetTree(request?: AssetTreeFlattenRequest | null): boolean {

    return (request?.filters ?? []).some(filter => {

        // Mirror the query builder: empty values are not applied, so they must not flatten either.
        if (filter?.value === undefined || filter?.value === null || filter?.value === '') return false;

        const field = filter.fieldId ?? filter._id ?? '';

        // Pseudo-filters (e.g. __advancedFilter) are not applied by the asset items query.
        if (field.startsWith('__')) return false;

        const rootField = field.split('.')[0];
        return !ASSET_SUBTREE_INVARIANT_FIELDS.includes(rootField);
    });
}
